import React, { useEffect, useMemo, useState, useRef } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import queryString from 'query-string';
import * as R from 'ramda';

import GlobalStyle from './GlobalStyle';
import SearchBar from './SearchBar';
import ToggleButtons from './ToggleButtons';
import TabSwitcher from './TabSwitcher';
import SortButton from './SortButton';
import ProjectItem from './ProjectItem';
import ToggleableButton from './ToggleableButton';

import { Label, DivWithLayoutTools, FilterSplitLine, FilterList, StateIndicator, LoadingIndicator } from './other-components';

const ProjectVisualization = ({ types, status, perPage = 10, user, ...props }) => {
    
    const maxPage = useRef(0);
    const fetchDelayTimer = useRef(null);
    const [ page, setPage ] = useState(1);
    const [ selectTab, setSelectTab ] = useState(0);
    const [ keyword, setKeyword ] = useState('');
    const [ selectStatus, setSelectStatus ] = useState(['進行中', '提案中']);
    const [ selectType, setSelectType ] = useState([]);
    const [ sort, setSort ] = useState(null);
    const [ sortMethod, setSortMethod ] = useState(null);
    const [ excludeInternalAffairs, setExcludeInternalAffairs ] = useState(null);
    const [ query, setQuery ] = useState(null);

    const viewMode = props['view-mode'] || 'normal';

    useEffect(() => {

        clearTimeout(fetchDelayTimer.current);

        fetchDelayTimer.current = setTimeout(() => {
            setQuery(getQueryString());
            setPage(1);
        }, 500);
        
    }, [sort, selectStatus, selectType, keyword, selectTab, sortMethod, excludeInternalAffairs]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [])

    const getQueryString = () => queryString.stringify({
        sort: sort,
        sort_method: sortMethod,
        status: selectStatus,
        type: selectType,
        keyword: keyword,
        exclude_type: excludeInternalAffairs ? '內部事務' : null,
        all: selectTab === 1
    }, {
        arrayFormat: 'bracket',
        skipNull: true,
        skipEmptyString: true
    })

    const { data, error } = useSWR(query && `/api/project-report/list?${query}`, axios, {
        revalidateOnReconnect: false,
        revalidateOnFocus: false
    });

    const postData = useMemo(() => {

        let rtn = data?.data?.map?.(_ => _.data);

        if (sort === null && rtn && user?.name) {
            rtn = rtn.map?.(item => {
                const byStartAt = R.descend(R.prop('start_at'));
                const isMe = workHour => workHour.user_name === user.name;
                const a = R.sort(byStartAt, R.filter(isMe, item.project_work_hours));

                return {...item, nearestWorkDate: a?.[0]?.start_at };
            });
            rtn = R.sort(R.descend(R.prop('nearestWorkDate')), rtn);
        }
        return rtn
    }, [data]);

    useEffect(() => {
        maxPage.current = ~~((data?.data?.length || 0) / perPage) + 1;
    }, [data]);

    const handleScroll = () => {
        if (window.scrollY + window.innerHeight >= document.body.clientHeight) {
            setPage(prevPage => {
                if (prevPage < maxPage.current) return prevPage + 1;
                return prevPage;
            })
        }
    }

    return (
        <DivWithLayoutTools>

            <GlobalStyle/>

            <SearchBar value={keyword} setValue={setKeyword} />

            <FilterList className="mt-3" label="工作項目/專案" borderBottom>
                <TabSwitcher value={selectTab} setValue={setSelectTab} options={[ '我有參與的工作項目/專案', '全部工作項目/專案' ]} />
            </FilterList>

            <FilterList label="狀態" borderBottom>
                <ToggleButtons value={selectStatus} setValue={setSelectStatus} options={status} />
            </FilterList>

            <FilterList className="" label="分類">
                <ToggleButtons value={selectType} setValue={setSelectType} options={types} />
            </FilterList>

            <div className="mt-5 d-flex align-center justify-between">
                { postData !== undefined && <Label>共有 { postData?.length || 0 } 筆資料</Label> }
                { postData === undefined && <Label></Label> }
                <FilterList label="排序" noMarginBottom>
                    <SortButton sortKey="end_date" nowSortKey={sort} setSortKey={setSort} nowSortMethod={sortMethod} setSortMethod={setSortMethod} className="mr-2">結案時間</SortButton>
                    <SortButton sortKey="start_date" nowSortKey={sort} setSortKey={setSort} nowSortMethod={sortMethod} setSortMethod={setSortMethod}>開案時間</SortButton>
                    <FilterSplitLine/>
                    <ToggleableButton value={excludeInternalAffairs} setValue={setExcludeInternalAffairs}>排除內部事務</ToggleableButton>
                </FilterList>
            </div>

            <ul className="mt-3">
                { postData?.slice(0, page * perPage).map( (projectData, index) =>
                    <ProjectItem key={`pji${projectData.id || projectData.project.uuid}`}
                        { ...projectData.project }
                        project_work_hours={projectData.project_work_hours}
                        project_costs={projectData.project_costs}
                        viewMode={viewMode}
                    />
                )}
                { postData?.length === 0 &&
                    <StateIndicator>設定的條件沒有資料</StateIndicator>
                }
                { postData === undefined && 
                    <LoadingIndicator/>
                }
            </ul>

        </DivWithLayoutTools>
    )
}



export default ProjectVisualization;
